import { useState, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { useLocation } from "react-router-dom";
import { publicApiClient } from "../../../api/apiClient";
import "./Event.css";

const FlowerDot = () => (
  <svg
    width="23"
    height="23"
    viewBox="-10 -10 120 120"
    fill="#F3D3DC"
    xmlns="http://www.w3.org/2000/svg"
  >
    <circle cx="50" cy="20" r="29" />
    <circle cx="80" cy="38" r="29" />
    <circle cx="69" cy="72" r="29" />
    <circle cx="31" cy="72" r="29" />
    <circle cx="20" cy="38" r="29" />
  </svg>
);

const ITEMS_PER_PAGE = 12;

const normalizeImageUrl = (url) => {
  if (!url) return "";

  return url;
};

const formatDate = (value) => {
  if (!value) return "";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return String(value).slice(0, 10).replaceAll("-", ".");
  }

  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const dd = String(date.getDate()).padStart(2, "0");

  return `${yyyy}.${mm}.${dd}`;
};

const formatPeriod = (startDate, endDate) => {
  const start = formatDate(startDate);
  const end = formatDate(endDate);

  if (!start && !end) return "-";
  if (start && !end) return start;
  if (!start && end) return end;
  if (start === end) return start;

  return `${start}~${end}`;
};

const getTimeValue = (value) => {
  if (!value) return 0;

  const date = new Date(value);
  const time = date.getTime();

  return Number.isNaN(time) ? 0 : time;
};

const getListFromResponse = (result) => {
  const data = result?.data || result;

  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.content)) return data.content;
  if (Array.isArray(data?.items)) return data.items;
  if (Array.isArray(data?.list)) return data.list;
  if (Array.isArray(data?.data)) return data.data;
  if (Array.isArray(data?.data?.content)) return data.data.content;

  return [];
};

const getMainImage = (item) => {
  const mainImg =
    item.mainImg ||
    item.mainImgUrl ||
    item.mainImageUrl ||
    item.thumbnailUrl ||
    item.imageUrl ||
    item.eventImageUrl ||
    item.fileUrl ||
    item.imgUrl ||
    "";

  const normalizedMainImg = normalizeImageUrl(mainImg);

  if (normalizedMainImg) return normalizedMainImg;

  return "https://placehold.co/300x380/F8EFF2/B05B75?text=Event";
};

const getImages = (item) => {
  const mainImg =
    item.mainImg ||
    item.mainImgUrl ||
    item.mainImageUrl ||
    item.thumbnailUrl ||
    item.imageUrl ||
    item.eventImageUrl ||
    item.fileUrl ||
    item.imgUrl ||
    "";

  const normalizedMainImg = normalizeImageUrl(mainImg);

  const detailImages = (item.images || [])
    .map((image) => {
      if (typeof image === "string") {
        return normalizeImageUrl(image);
      }

      return normalizeImageUrl(
        image.url ||
          image.imageUrl ||
          image.fileUrl ||
          image.imgUrl ||
          image.path ||
          ""
      );
    })
    .filter(Boolean);

  const result = [];

  if (normalizedMainImg) {
    result.push(normalizedMainImg);
  }

  detailImages.forEach((url) => {
    if (!result.includes(url)) {
      result.push(url);
    }
  });

  if (result.length > 0) return result;

  return ["https://placehold.co/600x800/F8EFF2/B05B75?text=Event"];
};

const normalizeEvent = (item) => {
  const id = item.eventId || item.event_id || item.id;
  const title = item.eventName || item.title || "";

  return {
    id,
    category: item.title || "총학생회 행사",
    title,
    date: formatPeriod(item.startDate, item.endDate),
    rawStartDate: item.startDate || "",
    createdAtRaw: item.createdAt || item.created_at || "",
    main_image: getMainImage(item),
    images: getImages(item),
    description:
      item.introduction ||
      item.content ||
      item.description ||
      item.body ||
      "",
    outline: item.overview || item.outline || item.summary || "",
    program: item.detailProgram || item.program || item.detail || "",
  };
};

export default function EventGallery() {
  const location = useLocation();

  const [search, setSearch] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [visibleCount, setVisibleCount] = useState(ITEMS_PER_PAGE);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [modalLoading, setModalLoading] = useState(false);

  const [events, setEvents] = useState([]);
  const [autoOpenedEventId, setAutoOpenedEventId] = useState(null);

  const loadMissingDetailContents = async () => {
    const hasMissingDetail = events.some(
      (event) =>
        event.id && !event.description && !event.outline && !event.program
    );

    if (!hasMissingDetail) return;

    try {
      setLoading(true);

      const nextEvents = await Promise.all(
        events.map(async (event) => {
          if (event.description || event.outline || event.program) {
            return event;
          }

          try {
            const result = await publicApiClient(
              `/api/activity/event/${event.id}`,
              {
                method: "GET",
              }
            );

            const detailData = result?.data || result;

            if (!detailData) return event;

            return {
              ...event,
              ...normalizeEvent({
                ...event,
                ...detailData,
              }),
            };
          } catch (error) {
            console.error("행사 상세 내용 조회 실패:", error);
            return event;
          }
        })
      );

      setEvents(nextEvents);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const timer = setTimeout(async () => {
      await loadMissingDetailContents();

      setSearchQuery(search);
      setVisibleCount(ITEMS_PER_PAGE);
    }, 300);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  useEffect(() => {
    const fetchEvents = async () => {
      try {
        setLoading(true);

        const result = await publicApiClient(
          "/api/activity/event?page=0&size=100&sort=createdAt,desc",
          {
            method: "GET",
          }
        );

        console.log("행사 목록 조회 성공:", result);

        const list = getListFromResponse(result)
          .map(normalizeEvent)
          .filter((item) => item.id);

        setEvents(list);
      } catch (error) {
        console.error("행사 목록 조회 실패:", error);
        setEvents([]);
      } finally {
        setLoading(false);
      }
    };

    fetchEvents();
  }, []);

  const handleOpenModal = useCallback(async (event) => {
    setSelected(event);

    try {
      setModalLoading(true);

      const result = await publicApiClient(`/api/activity/event/${event.id}`, {
        method: "GET",
      });

      console.log("행사 상세 조회 성공:", result);

      const detailData = result?.data || result;

      if (detailData) {
        setSelected(normalizeEvent(detailData));
      }
    } catch (error) {
      console.error("행사 상세 조회 실패:", error);
    } finally {
      setModalLoading(false);
    }
  }, []);

  useEffect(() => {
    const openEventId = location.state?.openEventId;

    if (!openEventId || events.length === 0) return;

    if (String(autoOpenedEventId) === String(openEventId)) return;

    const targetEvent = events.find(
      (event) => String(event.id) === String(openEventId)
    );

    if (!targetEvent) return;

    setAutoOpenedEventId(openEventId);
    handleOpenModal(targetEvent);
  }, [location.state, events, autoOpenedEventId, handleOpenModal]);

  const sorted = [...events].sort((a, b) => {
    const timeA = getTimeValue(a.rawStartDate);
    const timeB = getTimeValue(b.rawStartDate);

    if (timeA !== timeB) {
      return timeB - timeA;
    }

    return Number(a.id || 0) - Number(b.id || 0);
  });

  const filtered = sorted.filter((event) => {
    const keyword = searchQuery.trim();

    if (!keyword) return true;

    return (
      event.title.includes(keyword) ||
      event.category.includes(keyword) ||
      event.date.includes(keyword) ||
      event.description.includes(keyword) ||
      event.outline.includes(keyword) ||
      event.program.includes(keyword)
    );
  });

  const visible = filtered.slice(0, visibleCount);
  const total = filtered.length;
  const hasMore = visibleCount < total;

  useEffect(() => {
    if (selected) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }

    return () => {
      document.body.style.overflow = "";
    };
  }, [selected]);

  return (
    <div className="gallery-page">
      <div className="event-title-section">
        <h2 className="event-title">행사</h2>
      </div>

      <div className="gallery-topbar">
        <div className="search-wrap">
          <input
            type="text"
            placeholder="검색어를 입력하세요."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />

          <svg
            width="15"
            height="15"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            style={{ cursor: "pointer" }}
          >
            <circle cx="11" cy="11" r="8" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
        </div>
      </div>

      <div className="gallery-grid">
        {loading ? (
          Array(12)
            .fill(0)
            .map((_, i) => (
              <div className="skeleton-card" key={i}>
                <div className="skeleton-img" />
                <div className="skeleton-body">
                  <div className="skeleton-line short" />
                  <div className="skeleton-line" />
                  <div className="skeleton-line medium" />
                </div>
              </div>
            ))
        ) : (
          <>
            {visible.length === 0 && (
              <p className="gallery-empty">작성된 글이 없습니다.</p>
            )}

            {visible.map((event, idx) => (
              <div
                className="gallery-card"
                key={event.id}
                style={{
                  animationDelay: `${(idx % ITEMS_PER_PAGE) * 60}ms`,
                }}
                onClick={() => handleOpenModal(event)}
              >
                <div className="card-img-wrap">
                  <img
                    src={event.main_image}
                    alt={event.title}
                    className="card-img"
                  />
                </div>

                <div className="card-body">
                  <p className="card-category">{event.category}</p>
                  <h3 className="card-title">{event.title}</h3>
                  <p className="card-date">{event.date}</p>
                </div>
              </div>
            ))}
          </>
        )}
      </div>

      {!loading && hasMore && (
        <div className="gallery-more">
          <button onClick={() => setVisibleCount((v) => v + ITEMS_PER_PAGE)}>
            더보기 ({visibleCount}/{total})
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="3"
            >
              <polyline points="3 8 12 16 21 8" />
            </svg>
          </button>
        </div>
      )}

      {selected &&
        createPortal(
          <div className="modal-backdrop" onClick={() => setSelected(null)}>
            <div className="modal" onClick={(e) => e.stopPropagation()}>
              <div className="modal-scroll">
                <div className="modal-header">
                  <h2 className="modal-header-title">행사 소개</h2>

                  <button
                    className="modal-close"
                    onClick={() => setSelected(null)}
                  >
                    ✕
                  </button>
                </div>

                <div className="modal-title-section">
                  {selected.category && (
                    <p className="modal-category">{selected.category}</p>
                  )}

                  {selected.title && (
                    <h3 className="modal-title">{selected.title}</h3>
                  )}

                  {selected.date && (
                    <p className="modal-date">{selected.date}</p>
                  )}
                </div>

                {modalLoading ? (
                  <p className="gallery-empty">상세 내용을 불러오는 중입니다.</p>
                ) : (
                  <>
                    <div
                      className={`modal-img-grid count-${
                        selected.images?.length || 1
                      }`}
                    >
                      {(selected.images || [selected.main_image]).map(
                        (img, i) => (
                          <div className="modal-img-item" key={i}>
                            <img
                              src={img}
                              alt={`${selected.title} ${i + 1}`}
                            />
                          </div>
                        )
                      )}
                    </div>

                    {selected.description && (
                      <div className="modal-section">
                        <div className="modal-section-title">
                          <FlowerDot />
                          행사 소개
                        </div>
                        <p className="modal-section-content">
                          {selected.description}
                        </p>
                      </div>
                    )}

                    {selected.outline && (
                      <div className="modal-section">
                        <div className="modal-section-title">
                          <FlowerDot />
                          행사 개요
                        </div>
                        <p className="modal-section-content">
                          {selected.outline}
                        </p>
                      </div>
                    )}

                    {selected.program && (
                      <div className="modal-section">
                        <div className="modal-section-title">
                          <FlowerDot />
                          상세 프로그램
                        </div>
                        <p className="modal-section-content">
                          {selected.program}
                        </p>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>,
          document.body
        )}
    </div>
  );
}